import { Box, Button, createStyles, Select } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomPagination from "../../../../components/Pagination/CustomPagination";
import { getMar, getMarLogsNew } from "../../../../services/mar";
import UserDetail from "../MarTab/UserDetail";
import HideClient from "../ClientHide";
import ClientColumn from "../ClientColumn";
import { decrypt, formatDate, formatTime } from "../../../../utils";
import { CgSortZa } from "react-icons/cg";
import { CSVLink, CSVDownload } from "react-csv";

import moment from "moment";
import MarLogTable, { getDescriptionFields } from "./MarLogTable/indexNew";
import store from "../../../../store";
import { setLogType } from "../../../../store/reducers/mar";
import { IconChevronDown } from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    // overflowX: "auto",
    marginBottom: "50px",
    height: "100%",
  },
  btnContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    "& button": {
      border: "none",
      padding: "5px 30px",
      height: "50px",
      color: "black",
    },
    "& img": {
      width: "30px",
    },
    [theme.fn.smallerThan("sm")]: {
      "& button": {
        height: "40px",
        padding: "5px 16px",
        fontSize: "13px",
      },
      "& img": {
        width: "22px",
      },
    },
  },
  mainHeading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  contentBody: {
    width: "100%",
    display: "flex",
    gap: "20px",
    // marginTop: "30px",
    [theme.fn.smallerThan("md")]: {
      flexDirection: "column",
      gap: "12px",
    },
  },
  scheduleDetailRight: {
    width: "calc(100% - 270px)",
    [theme.fn.smallerThan("md")]: {
      width: "100%",
    },
  },
  scheduleDetailRight100: {
    width: "calc(100% - 70px)",
    [theme.fn.smallerThan("md")]: {
      width: "100%",
    },
  },
  userDetail: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // marginTop: "40px",
    "& .detail_box": {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      "& .image_box": {
        width: "80px",
        height: "80px",
        border: "1px solid var(--app-border)",
        borderRadius: "10px",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      },
      "& h3": {
        margin: "0",
        fontSize: "30px",
        textAlign: "center",
      },
      "& p": {
        margin: "0",
        color: "var(--app-text-muted)",
        textAlign: "center",
      },
    },
  },
  tableParentBox: {
    height: "100%",
    backgroundColor: "var(--app-surface)",
    padding: "12px 8px",
    borderRadius: 16,
    width: "100%",
    overflowX: "auto",
    [theme.fn.smallerThan("sm")]: {
      padding: "8px 4px",
      borderRadius: 8,
    },
  },
  filterSelect: {
    width: 110,
    display: "flex",
    marginLeft: "auto",
    marginBottom: "20px",
    "& .mantine-Select-input ": {
      border: "none",
      borderRadius: 8,
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 400,
      color: "var(--app-text-muted)",
    },
    "& .mantine-Select-rightSection svg": {
      fontSize: 24,
      color: "var(--app-text-muted)",
    },
    [theme.fn.smallerThan("sm")]: {
      width: "100%",
      marginLeft: 0,
    },
  },
}));

const MarLogTab = ({ refresh, userId }) => {
  const { classes } = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  // const { count, newMarLogs } = useSelector((state) => state.mar);
  const [show, setShow] = useState(true);
  const {
    selectedClient,
    filterDates,
    allData,
    logType,
    logSearch,
    newMarLogs,
    medicationLogs,
  } = useSelector((state) => state.mar);
  const { profile } = useSelector((state) => state?.profile);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // When search text changes, reset to page 1 so filtered results start from the beginning
  const prevLogSearch = useRef(logSearch);
  useEffect(() => {
    if (prevLogSearch.current !== logSearch) {
      prevLogSearch.current = logSearch;
      setCurrentPage(1);
    }
  }, [logSearch]);

  useEffect(() => {
    // if (refresh) getMarLog();
    getMarLog();
  }, [currentPage, refresh, selectedClient, logType, logSearch, filterDates]);

  const getMarLog = async () => {
    await getMarLogsNew(currentPage, null, true, logSearch, filterDates); // await getMar(
    //   currentPage,
    //   null,
    //   true,
    //   filterDates,
    //   logType,
    //   undefined,
    //   true,
    // );
  };

  const formatDescriptionForCSV = (fields) => {
    if (!fields || fields.length === 0) return "-";

    return fields
      .filter(
        (field) =>
          field.value &&
          field.value !== "-" &&
          field.value !== "null" &&
          field.value !== "undefined",
      )
      .map((field) => `${field.label}: ${field.value}`)
      .join(" | ");
  };

  // Generate CSV data that matches the table exactly
  const csvData = useMemo(() => {
    if (!newMarLogs?.allData || newMarLogs?.allData?.length === 0) {
      return [];
    }

    // Define CSV headers matching the table columns
    const headers = [
      "Date",
      "Time",
      "Action",
      "Client Name",
      "Medication Name",
      "Action by",
      "Description",
    ];

    // Process the data exactly as shown in the table
    const processedData = newMarLogs?.allData
      .slice(0)
      .sort((a, b) => new Date(b?.date) - new Date(a?.date))
      .filter((v) => v?.medicationType !== "discontinued")
      .map((row) => {
        const descriptionFields = getDescriptionFields(row);
        const descriptionText = formatDescriptionForCSV(descriptionFields);

        return {
          Date: formatDate(row?.createdAt) || "-",
          Time: formatTime(row?.createdAt) || "-",
          Action: row?.logAction || "-",
          "Client Name": decrypt(row?.client?.name) || "-",
          "Medication Name": row?.form3?.medicationName || "-",
          "Action by": row?.createdBy?.name || "-",
          Description: descriptionText,
        };
      });

    return [headers, ...processedData?.map((row) => Object.values(row))];
  }, [newMarLogs]);

  return (
    <div className={classes.content}>
      {/* {!userId ? (
        <HideClient show={show} setShow={setShow} />
      ) : (
        <Box m={20}></Box>
      )} */}

      <Box>
        <Select
          className={classes.filterSelect}
          onChange={(val) => {
            store.dispatch(setLogType(val));
          }}
          defaultValue={"all"}
          data={[
            { value: "all", label: "All" },
            { value: "ytd", label: "YTD" },
            { value: "last_year", label: "Last Year" },
          ]}
          rightSection={<CgSortZa />}
          rightSectionWidth={30}
          styles={{ rightSection: { pointerEvents: "none" } }}
        />
        <div className={classes.contentBody}>
          {!userId && <UserDetail isClient={false} />}
          <div className={classes.tableParentBox}>
            <MarLogTable />
            <CustomPagination
              data={newMarLogs?.count}
              itemsPerPage={10}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      </Box>
      {/* <div className={classes.btnContainer}>
        <CSVLink
          data={csvData}
          filename={`Medication_Log_${moment().format("YYYY-MM-DD")}.csv`}
        >
          <Button
            leftIcon={
              <img src={"/assets/images/downloadFolder.png"} alt="download" />
            }
            color="yellow"
            title="Download Log"
          >
            Download CSV
          </Button>
        </CSVLink>
      </div> */}
    </div>
  );
};

export default MarLogTab;
